import { useState, useRef, useCallback, useMemo } from 'react';
import { Terminal, Shield, Loader2, Play, Plus, Download, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/Table';
import { useUser } from '../../context/UserContext';
import { runHuntingQuery } from '../../services/investigation';
import type { HuntingCapability, HuntingQueryResult } from '../../data/types';
import { generateHints } from '../../lib/huntingTableCapabilities';
import { ensureUtcDesignator } from '../../lib/huntingFormat';
import { CapabilityStrip } from './CapabilityStrip';

interface EntitySnippet {
  type: string;
  name: string;
  value: string;
}

interface KqlEditorProps {
  tenantId: string;
  incidentId?: string;
  alertIds?: string[];
  entities?: EntitySnippet[];
  /**
   * Tenant capability snapshot from /api/investigate/{tenantId}/capabilities.
   * Drives the strip above the editor and the empty-state hint logic.
   * Pass `null` when no snapshot exists yet — the strip won't render and
   * no hints will fire (fail-open).
   */
  capabilities?: Partial<Record<HuntingCapability, boolean>> | null;
  /** ISO timestamp of the snapshot; rendered alongside the strip. */
  capabilitiesRefreshedAt?: string | null;
}

const ALLOWED_ROLES = ['analyst', 'senior_analyst', 'admin'];

export function KqlEditor({
  tenantId,
  incidentId,
  alertIds,
  entities,
  capabilities,
  capabilitiesRefreshedAt,
}: KqlEditorProps) {
  const { currentUser } = useUser();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<HuntingQueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canRunCustomKql = currentUser && ALLOWED_ROLES.includes(currentUser.role);

  // Empty-result hints: when a query returns 0 rows, check if the KQL
  // references any table that the tenant either can't query (warning) or
  // can only query partially (info, e.g. Cloud Apps Discovery). Memoised
  // off the actual result so the hints update when a new query runs.
  const emptyResultHints = useMemo(() => {
    if (!result || result.results.length !== 0) return [];
    return generateHints(query, capabilities ?? null);
  }, [result, query, capabilities]);

  const insertAtCursor = useCallback((text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = query.slice(0, start);
    const after = query.slice(end);
    const newQuery = before + text + after;
    setQuery(newQuery);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = start + text.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  }, [query]);

  const handleRun = useCallback(async () => {
    if (!query.trim() || isLoading) return;
    setIsLoading(true);
    setError(null);
    setResult(null);

    const response = await runHuntingQuery(tenantId, query);

    if (!response.success) {
      console.error('KQL query failed', response.error);
      if (response.error?.includes('Insufficient permissions')) {
        setError('You do not have permission to run custom KQL queries. This feature requires Analyst, Senior Analyst, or Admin role.');
      } else {
        setError(response.error || 'Query failed');
      }
    } else {
      setResult(response.data);
    }
    setIsLoading(false);
  }, [tenantId, query, isLoading]);

  const handleDownloadCsv = useCallback(() => {
    if (!result || result.results.length === 0) return;
    // Defender `datetime` columns arrive from Graph as timezone-naive UTC
    // strings; ensureUtcDesignator appends a `Z` so the export is unambiguously
    // UTC for cross-tool correlation (the on-screen table converts the same
    // instant to local time for display). Non-timestamp cells are unchanged.
    const escape = (val: unknown) => {
      const str = val == null ? '' : String(ensureUtcDesignator(val));
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = result.columns.map(escape).join(',');
    const rows = result.results.map((row) =>
      result.columns.map((col) => escape(row[col])).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kql-results-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [result]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  }, [handleRun]);

  // Locked card for unauthorized roles
  if (!canRunCustomKql) {
    return (
      <Card className="mt-4">
        <CardContent className="py-8 text-center">
          <Shield className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="font-medium text-text-primary">Custom KQL Queries</p>
          <p className="text-sm text-text-secondary mt-1">
            Requires Analyst, Senior Analyst, or Admin role. Contact your administrator for access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4" padding="none">
      <CardContent>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <Terminal className="w-4 h-4 text-accent-blue" />
          <span className="font-medium text-text-primary text-sm">Custom KQL Query</span>
        </div>

        {/* Tenant capability strip — pre-flight visibility of which Defender
            tables this tenant can actually serve. Hidden when no snapshot
            is available (fail-open). */}
        {capabilities !== undefined && (
          <CapabilityStrip
            capabilities={capabilities ?? null}
            lastRefreshedAt={capabilitiesRefreshedAt ?? null}
          />
        )}

        {/* Context snippet buttons */}
        <div className="flex flex-wrap gap-1.5 px-4 pb-2">
          {incidentId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => insertAtCursor(`| where IncidentId == "${incidentId}"`)}
            >
              <Plus className="w-3 h-3" /> Incident ID
            </Button>
          )}
          {alertIds && alertIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => insertAtCursor(`| where AlertId in (${alertIds.map(id => `"${id}"`).join(', ')})`)}
            >
              <Plus className="w-3 h-3" /> Alert IDs
            </Button>
          )}
          {entities?.map((entity) => (
            <Button
              key={`${entity.type}-${entity.value}`}
              variant="ghost"
              size="sm"
              onClick={() => {
                const filter = entity.type === 'user'
                  ? `| where AccountUpn =~ "${entity.value}"`
                  : entity.type === 'device'
                  ? `| where DeviceName =~ "${entity.value}"`
                  : entity.type === 'ip'
                  ? `| where IPAddress == "${entity.value}"`
                  : `| where SenderFromAddress =~ "${entity.value}"`;
                insertAtCursor(filter);
              }}
            >
              <Plus className="w-3 h-3" /> {entity.name}
            </Button>
          ))}
        </div>

        {/* Textarea */}
        <div className="px-4">
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={8}
            placeholder={"// Enter your KQL query here\n// Example: DeviceProcessEvents\n//   | where Timestamp > ago(7d)\n//   | take 100\n//\n// Press Cmd+Enter (Ctrl+Enter) to run"}
            className="w-full bg-bg-tertiary text-text-primary font-mono text-sm rounded-lg border border-border-default p-3 resize-y focus:outline-none focus:ring-2 focus:ring-brand-navy/50 dark:focus:ring-brand-lime/50 placeholder:text-text-muted"
            spellCheck={false}
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-xs text-text-muted">
            {query.length.toLocaleString()} / 10,000 characters
          </span>
          <Button
            variant="primary"
            size="sm"
            disabled={!query.trim() || isLoading}
            onClick={handleRun}
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
            {isLoading ? 'Running...' : 'Run Query'}
          </Button>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-4 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-xs font-medium text-red-400 mb-1">Query Failed</p>
            <pre className="text-sm text-red-400 whitespace-pre-wrap break-words font-mono">{error}</pre>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="px-4 pb-4 space-y-2">
            {/* Metadata bar */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-text-primary">Results</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-text-secondary">
                  {result.resultCount} results &bull; {result.executionTimeMs}ms
                </span>
                {result.results.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleDownloadCsv}>
                    <Download className="w-3.5 h-3.5" /> CSV
                  </Button>
                )}
              </div>
            </div>

            {result.results.length === 0 ? (
              <div className="space-y-2">
                <div className="p-6 bg-bg-tertiary rounded-lg text-center">
                  <p className="text-text-secondary">No results found</p>
                </div>
                {emptyResultHints.map((hint) => (
                  <div
                    key={`${hint.table}-${hint.severity}`}
                    className={
                      'p-3 rounded-lg border flex items-start gap-2 ' +
                      (hint.severity === 'warning'
                        ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300'
                        : 'bg-blue-50 border-blue-300 text-blue-800 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300')
                    }
                  >
                    {hint.severity === 'warning' ? (
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    )}
                    <p className="text-xs leading-relaxed">{hint.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="max-h-[70vh] overflow-auto rounded-lg border border-border-default">
                  <Table className="border-0 rounded-none overflow-visible">
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow hover={false}>
                        {result.columns.map((col) => (
                          <TableHead key={col} className="whitespace-nowrap">
                            {col}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.results.slice(0, 50).map((row, idx) => (
                        <TableRow key={idx}>
                          {result.columns.map((col) => (
                            <TableCell
                              key={col}
                              mono
                              className="whitespace-nowrap align-top"
                            >
                              <span title={String(row[col] ?? '')}>
                                {row[col] != null ? String(row[col]) : '-'}
                              </span>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {result.results.length > 50 && (
                  <div className="p-2 text-center text-xs text-text-secondary bg-bg-tertiary rounded-lg">
                    Showing 50 of {result.resultCount} results &bull; download CSV for the full set
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
